import { useTranslation } from "react-i18next";
import {
  AccordionListItem,
  type AccordionListItemData,
  type AccordionListItemGroup,
  type AccordionListItemGroupAction,
  ColorPicker,
  LabelledSwitch,
  useDiscardModal,
} from "senf-atomic-design-system";
import Select from "senf-atomic-design-system/src/components/core/select/Select";
import Seperator from "senf-atomic-design-system/src/components/core/seperator/Seperator";
import { useFeatureAttributes } from "../functions/useFeatureAttributes";

// Minimal interface for accordion item (not actually used for content)
interface GisConfigSection extends AccordionListItemData {
  name: string;
}

const ColorSelector = ({ legendAttributes, handleColorChange }) => (
  <div className="flex flex-col gap-2">
    {legendAttributes
      ?.sort((a, b) => {
        if (a?.title < b?.title) {
          return -1; // a comes before b
        }
        if (a?.title > b?.title) {
          return 1; // b comes before a
        }
        return 0; // a and b are equivalent
      })
      ?.map((layer) => (
        <div className="flex items-center gap-2" key={layer.title}>
          <ColorPicker
            initialColor={layer.color}
            initialOpacity={layer?.opacity}
            handleSave={async (color, opacity) => {
              await Promise.resolve(
                handleColorChange(layer.title, color, opacity),
              );
            }}
          />
          <span className="body-md">{layer.title}</span>
        </div>
      ))}
  </div>
);

// Content component for label configuration
const LabelConfigContent = ({ data, tooltipAttributes, formik, index, t }) => (
  <Select
    labelOption="inline"
    label={`${t("global:label")}:`}
    text={
      tooltipAttributes.find(({ value }) => value === data?.selectedTooltip)
        ?.label || t("global:none")
    }
    selectState="focus"
    onValueChange={(value) => {
      const newData = { ...data.data };

      newData.features = newData.features.map((feature) => {
        return {
          ...feature,
          properties: {
            ...feature.properties,
            tooltipLabel:
              (typeof value === "string" && feature.properties?.[value]) ||
              value ||
              null,
          },
        };
      });

      formik.setFieldValue(`gisData[${index}].selectedTooltip`, value);
      formik.setFieldValue(`gisData[${index}].data`, newData);
    }}
    data={tooltipAttributes.map(({ value, label }) => ({
      value,
      text: label,
    }))}
    useTriggerWidth={true}
  />
);

// Content component for color configuration
const ColorConfigContent = ({
  data,
  colorAttributes,
  formik,
  index,
  t,
  colorPalette,
  legendAttributes,
  handleColorChange,
  generalColor,
}) => (
  <div className="flex flex-col gap-3">
    <Select
      text={
        colorAttributes?.find(({ value }) => value === data?.selectedAttribute)
          ?.label || t("map:builder.colorizeAllSame")
      }
      labelOption="inline"
      label={`${t("global:color")}:`}
      selectState="focus"
      onValueChange={(value) => {
        const newData = { ...data?.data };

        const uniqueValues = [
          ...new Set(
            data?.data.features
              .map(
                (feature) =>
                  typeof value === "string" && feature.properties?.[value],
              )
              .filter(Boolean),
          ),
        ];
        const newLegendAttributes = uniqueValues.map((value, index) => ({
          title: value,
          color:
            legendAttributes?.find((layer) => layer.title === value)?.color ||
            colorPalette[index % colorPalette.length],
        }));

        newData.features = newData.features.map((feature) => {
          const relatedLayer = newLegendAttributes.find(
            (layer) =>
              layer.title ===
              (typeof value === "string" && feature.properties?.[value]),
          );

          return {
            ...feature,
            properties: {
              ...feature.properties,
              color: relatedLayer?.color,
            },
          };
        });

        formik.setFieldValue(
          `gisData[${index}].legendAttributes`,
          newLegendAttributes,
        );
        formik.setFieldValue(`gisData[${index}].selectedAttribute`, value);
        formik.setFieldValue(`gisData[${index}].data`, newData);
      }}
      data={colorAttributes?.map(({ value, label }) => ({
        value,
        text: label,
      }))}
      useTriggerWidth={true}
    />

    {data?.selectedAttribute === "none" && (
      <ColorSelector
        legendAttributes={[
          {
            color: data?.data?.features?.[0]?.properties?.color || generalColor,
            opacity: data?.data?.features?.[0]?.properties?.opacity || 1,
            title: t("global:color"),
          },
        ]}
        handleColorChange={handleColorChange}
      />
    )}

    {data?.selectedAttribute && data?.selectedAttribute !== "none" && (
      <ColorSelector
        legendAttributes={legendAttributes}
        handleColorChange={handleColorChange}
      />
    )}
  </div>
);

// Content component for legend settings
const LegendConfigContent = ({ data, formik, index }) => (
  <LabelledSwitch
    alignSwitch="left"
    name={`gisData[${index}].withLegend`}
    label={"In der Legende anzeigen"}
    labelStyle="body-md"
    toggled={data?.withLegend}
    handleSetToggle={() => {
      formik.setFieldValue(`gisData[${index}].withLegend`, !data.withLegend);
    }}
  />
);

const GisDataset = ({ formik, index, initialColor }) => {
  const colorPalette = [
    initialColor,
    "#fed062",
    "#bd98f6",
    "#91dff4",
    "#8dd9b8",
    "#929df6",
    "#e8907e",
    "#f6c095",
  ];

  const { t } = useTranslation();
  const { displayDiscardModal } = useDiscardModal();
  const { attributes, handleColorChange, generalColor } = useFeatureAttributes(
    formik,
    index,
    initialColor,
  );

  const colorAttributes = [...attributes];
  colorAttributes.unshift({
    value: "none",
    label: t("map:builder.colorizeAllSame"),
  });

  const tooltipAttributes = [...attributes];
  tooltipAttributes.unshift({
    value: "none",
    label: t("global:none"),
  });

  const data = formik.values.gisData?.[index];
  const legendAttributes = data?.legendAttributes;

  // Create the configuration content
  const configurationContent = (
    <div className="space-y-3">
      <LabelConfigContent
        data={data}
        tooltipAttributes={tooltipAttributes}
        formik={formik}
        index={index}
        t={t}
      />

      <ColorConfigContent
        data={data}
        colorAttributes={colorAttributes}
        formik={formik}
        index={index}
        t={t}
        colorPalette={colorPalette}
        legendAttributes={legendAttributes}
        handleColorChange={handleColorChange}
        generalColor={generalColor}
      />

      {data?.selectedAttribute && data?.selectedAttribute !== "none" && (
        <>
          <Seperator className="-mx-3" />
          <LegendConfigContent data={data} formik={formik} index={index} />
        </>
      )}
    </div>
  );

  // Transform into accordion group with custom content
  const groups: AccordionListItemGroup<GisConfigSection>[] = [
    {
      groupKey: data?.layerTitle || `gis-layer-${index}`,
      title: data?.layerTitle || `GIS Layer ${index + 1}`,
      subtitle: "GIS Datenset Konfiguration",
      items: [], // Empty items since we're using custom content
      isDefaultExpanded: true,
      customContent: configurationContent,
      actions: [
        {
          text: t("global:delete"),
          leadingIcon: "trash",
          critical: true,
          onClick: () => {
            displayDiscardModal({
              title: t("util:delete", { val: t("global:data") }),
              description: t("global:deleteDescription"),
              submitText: t("global:delete"),
              type: "onlyCloseDiscardModal",
              onSubmit: () => {
                formik.setFieldValue(
                  "gisData",
                  formik.values.gisData.filter((_, i) => i !== index),
                );
              },
            });
          },
        },
      ] as AccordionListItemGroupAction[],
    },
  ];

  return <AccordionListItem groups={groups} />;
};

export default GisDataset;
